import { CodeItem, CodetableItemState } from 'app/area/codetable/item/types-store';
import { CodetableMetadata } from 'app/area/codetable/types-store';
import { CodeComplexValue } from 'app/area/shared/codetable/types';
import { ValidationProps, ValidationLevel } from 'ui/common/validation/types';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { CodetableTranslation } from 'app/area/codetable/constants';
import { CodeActionType, CodeScreenValidate } from 'app/area/codetable/item/types';
import { FilterOption } from 'app/components/options-handler/types';

export interface ValidationData {
  codetableId: number;
  codeItem: CodeItem;
  uomConversionFactors?: CodeComplexValue<number>[];
  metadata: CodetableMetadata;
  translation: CodetableTranslation;
  validation: { [key: string]: ValidationProps };
  actionType: CodeActionType;
  validateScreen: CodeScreenValidate;
  options?: {
    [key: string]: FilterOption;
  };
  item: CodetableItemState;
}

export const validate = (validationData: ValidationData): { [key: string]: ValidationProps } => {
  const { codeItem, metadata, translation, validation, validateScreen } = validationData;
  const translationsValidation = validateDescriptions(codeItem, metadata, translation);
  const nodeValidation = validateNode(codeItem, metadata, translation);
  const categoryValidation = validateCategory(codeItem, metadata, translation);
  const parentValidation = !!validation ? validation.parent : undefined;
  const companyRegisterIdValidation = validateCompanyRegisterId(codeItem, metadata, translation);

  return {
    parent: parentValidation,
    ...translationsValidation,
    ...nodeValidation,
    ...categoryValidation,
    ...companyRegisterIdValidation,
    ...validateScreen(validationData)
  };
};

const validateDescriptions = (
  codeItem: CodeItem,
  metadata: CodetableMetadata,
  translation: CodetableTranslation
): { language: ValidationProps } => {
  if (metadata.features.description) {
    const emptyTranslations = codeItem.description.reduce((accumulator, description) =>
      description.description === '' || description.description === undefined ? [...accumulator, description.value] : accumulator, []);

    return emptyTranslations.length === 0
      ? { language: undefined }
      : {
        language: {
          level: ValidationLevel.error, messages: emptyTranslations.map(id => ({
            level: ValidationLevel.error,
            message: translation.must_be_filled_out,
            parameters: {
              description: id
            }
          }))
        }
      };
  }

  return { language: undefined };
};

const validateNode = (
  codeItem: CodeItem,
  metadata: CodetableMetadata,
  translation: CodetableTranslation
): { node: ValidationProps } => {
  if (!!metadata && !!metadata.codetableNode && isDefinedIncludingZero(metadata.codetableNode.id)) {
    return {
      node: !!codeItem.node && isDefinedIncludingZero(codeItem.node.value) ? undefined : {
        level: ValidationLevel.error,
        messages: [{
          level: ValidationLevel.error,
          message: translation.must_be_filled_out
        }]
      }
    };
  }

  return { node: undefined };
};

const validateCategory = (
  codeItem: CodeItem,
  metadata: CodetableMetadata,
  translation: CodetableTranslation
): { category: ValidationProps } => {
  if (isDefinedIncludingZero(metadata?.codetableCategory?.id)) {
    return {
      category: isDefinedIncludingZero(codeItem?.category?.value) ? undefined : {
        level: ValidationLevel.error,
        messages: [{
          level: ValidationLevel.error,
          message: translation.must_be_filled_out
        }]
      }
    };
  }

  return { category: undefined };
};

const validateCompanyRegisterId = (
  codeItem: CodeItem,
  metadata: CodetableMetadata,
  translation: CodetableTranslation
): { companyRegisterId: ValidationProps } => {
  if (!!metadata?.companyRegisterId?.required && !codeItem.companyRegisterId) {
    return {
      companyRegisterId: {
        level: ValidationLevel.error,
        messages: [{
          level: ValidationLevel.error,
          message: translation.must_be_filled_out
        }]
      }
    };
  }

  return { companyRegisterId: undefined };
};
